/**
 * The links that open a page with its conversation pane open on a chosen conversation (#5826).
 *
 * A work agreement page reads `?pane=correspondence`, and a proposal page reads `?pane=comments`.
 * Either one may add `&forum=`, which names the conversation to select: a forum id, the forum's name
 * ("All Stakeholders", "Client Team", "Vendor Team"), or, on a work agreement, the title its tab shows
 * ("Work Correspondence", "Client Team Correspondence", "Vendor Team Correspondence", #6054). A
 * conversation the reader cannot see is not an error; the pane opens on its default tab.
 *
 * The URL form is documented in docs/design/messaging/conversation-links.md, because pages outside
 * Messaging link to it (#4441).
 */

/** The search parameter that opens a page's conversation pane. */
export const conversationPaneParameter = "pane";
/** The search parameter that names the conversation the pane opens on. */
export const conversationForumParameter = "forum";

/** The conversation panes, by the value of `?pane=` that opens each. */
export const ConversationPane = {
  /** A work agreement's Correspondence pane: its three chats. */
  Correspondence: "correspondence",
  /** A proposal's Comments pane: its two comment conversations. */
  Comments: "comments",
} as const;
export type ConversationPane = (typeof ConversationPane)[keyof typeof ConversationPane];

/** The records whose pages open on one of their conversations. */
export const ConversationRecord = {
  WorkAgreement: "Work.Agreement",
  Proposal: "Work.Proposal",
} as const;
export type ConversationRecord = (typeof ConversationRecord)[keyof typeof ConversationRecord];

export function isConversationRecord(value: unknown): value is ConversationRecord {
  return value === ConversationRecord.WorkAgreement || value === ConversationRecord.Proposal;
}

/**
 * A request, from the page's URL, to open a conversation pane on one conversation.
 *
 * `forum` is a forum id, the forum's name, or the title its tab shows. Each new `key` selects the
 * conversation again — a second click on the same notification is a new request — and between
 * requests the reader's own choice of tab stands. A conversation the reader cannot see is not an
 * error: the pane stays on its default tab.
 */
export type ForumRequest = {
  forum: string;
  key: string;
};

/** What a page's URL asks of its conversation pane, when it asks anything. */
export type ConversationPaneRequest = {
  /** The conversation to select: a forum id or a conversation name. Absent for the default tab. */
  forum?: string;
};

/**
 * The request `search` makes of `pane`, or `undefined` when it does not ask to open it. A request for
 * another page's pane is not one for this page.
 */
export function readConversationPaneRequest(
  search: URLSearchParams,
  pane: ConversationPane,
): ConversationPaneRequest | undefined {
  if (search.get(conversationPaneParameter) !== pane) return undefined;
  const forum = search.get(conversationForumParameter)?.trim();
  return forum ? { forum } : {};
}

/** `search` with no request of the conversation pane left in it, as `?…`, or "" when nothing is left. */
export function withoutConversationPaneRequest(search: URLSearchParams): string {
  const remaining = new URLSearchParams(search);
  remaining.delete(conversationPaneParameter);
  remaining.delete(conversationForumParameter);
  const query = remaining.toString();
  return query ? `?${query}` : "";
}

function conversationQuery(pane: ConversationPane, forum: string | undefined): string {
  const query = new URLSearchParams({ [conversationPaneParameter]: pane });
  if (forum) query.set(conversationForumParameter, forum);
  return `?${query.toString()}`;
}

/**
 * A work agreement page with its Correspondence pane open on `forum`: a forum id or a conversation
 * name. An archived agreement is opened on its archived route, which is where the page would send it.
 */
export function workAgreementConversationPath(args: {
  workAgreementId: string;
  forum?: string;
  archived?: boolean;
}): string {
  const { workAgreementId, forum, archived } = args;
  return `/work/${archived ? "archived" : "active"}/view/${workAgreementId}/details${conversationQuery(
    ConversationPane.Correspondence,
    forum,
  )}`;
}

/** Where a proposal is, by its state. */
export type ProposalConversationStage = "drafts" | "active" | "archived";

/**
 * A proposal page with its Comments pane open on `forum`. The stage and the mode are the proposal's
 * and the reader's: a draft or a submitted proposal, edit for a draft's editor and review for one of its
 * reviewers. Only a draft has an editor, so a submitted proposal asked for in edit opens in view (#7073).
 * An archived proposal has only a view, so it opens in view whoever the reader is.
 */
export function proposalConversationPath(args: {
  proposalId: string;
  stage: ProposalConversationStage;
  mode: "view" | "review" | "edit";
  forum?: string;
}): string {
  const { proposalId, stage, mode, forum } = args;
  const hasMode = stage !== "archived" && (mode !== "edit" || stage === "drafts");
  const action = hasMode ? mode : "view";
  return `/proposals/${stage}/${action}/${proposalId}/details${conversationQuery(ConversationPane.Comments, forum)}`;
}

/** The generic chat dialog over Communications: where a conversation opens when its record cannot. */
export function forumDialogPath(forumId: string): string {
  return `/communications/forums/view/${forumId}`;
}

/** The search parameters of {@link conversationDestinationPath}. */
export const conversationRecordParameter = "record";
export const conversationRecordIdParameter = "recordId";

/**
 * The page that works out where a conversation opens for the reader, and sends them there: the
 * record's page with the pane open, or the chat dialog when they cannot open the record. A link
 * cannot say which by itself, because the answer is the reader's and changes over time — which
 * revision of an agreement they can see, whether a proposal is still a draft, whether they review it.
 */
export function conversationDestinationPath(args: {
  forumId: string;
  record: ConversationRecord;
  recordId: string;
}): string {
  const query = new URLSearchParams({
    [conversationRecordParameter]: args.record,
    [conversationRecordIdParameter]: args.recordId,
  });
  return `/conversations/${args.forumId}?${query.toString()}`;
}

/**
 * Where a listed conversation opens when it belongs to a record (#6585): through
 * {@link conversationDestinationPath}, which lands on the record's page scrolled to the oldest unread
 * message. `undefined` for a conversation with no record — a network or a direct chat — which keeps
 * the chat dialog. `topicClass` and `topicId` are the forum's topic, as `Topic` spells them.
 */
export function recordConversationPath(args: {
  forumId: string;
  topicClass: string | undefined;
  topicId: string | undefined;
}): string | undefined {
  const { forumId, topicClass, topicId } = args;
  if (!topicId || !isConversationRecord(topicClass)) return undefined;
  return conversationDestinationPath({ forumId, record: topicClass, recordId: topicId });
}
